// Portado pro anery-marca/Tailwind — ADR-0005, TP-031 T6/D5. Segue o padrão
// de features/captacao/DialogoDoCaso.tsx: wrapper `anery-marca`, cor só de
// token (contraste.test.ts reprova hex solto), `Dialog`/`Button` seguem MUI
// (comportamento). A tela continua 100% API real — nenhum dado mockado.

import { useState } from 'react';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import MoreVertOutlined from '@mui/icons-material/MoreVertOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as LinkDeRota, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { ConfirmacaoHumana } from '../../shared/ui/ConfirmacaoHumana';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { rotuloDoEstado } from './etapas';
import { SeloDaSituacaoDoPrazo } from './SeloDaSituacaoDoPrazo';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Identificador } from '../../shared/ui/Identificador';
import { Medida, Painel } from '../../shared/ui/primitivas';
import { ORIGEM_PADRAO, type OrigemDoCaso } from '../../shared/navegacao/mapaDeNavegacao';
import { PainelDasFrentes } from './PainelDasFrentes';
import { InformacoesDoCaso } from './informacoes/InformacoesDoCaso';
import { OQueFaltaParaCompletar } from './completude/OQueFaltaParaCompletar';
import { invalidarOQueAProvaMuda } from './completude/api';
import { formatarDataHora } from '../../shared/dominio/datas';
import { variaveisDaMarca } from '../../tema/variaveisDaMarca';
import { EsperasDoCaso } from './EsperasDoCaso';
import { EnviosAoIw } from './EnviosAoIw';
import { RetornosDoCaso } from './RetornosDoCaso';
import { VinculoComOIw } from './VinculoComOIw';
import { rotuloDoEventoDaLinhaDoTempo } from './linhaDoTempo';
import { ModalidadeDoCasoForm, TransicoesDoCaso } from './AcoesDoCaso';
import { AlcadaDaMargem } from './alcada/AlcadaDaMargem';
import { PainelDaPassagemDePlantao } from './passagem/PassagemDePlantao';
import { OrcamentoDoIw } from './avaliacao/OrcamentoDoIw';
import { modalidadeDoMotorDoCaso } from './avaliacao/tipos';
import {
  chavesDeCasos,
  opcoesDoCaso,
  opcoesDaLinhaDoTempoDoCaso,
  opcoesDaThreadDoCaso,
  opcoesDosCamposExtraidosDoCaso,
} from './api/casos';
import {
  ORIGEM_DO_PRAZO_DE_IMPLANTACAO,
  ORIGEM_DO_PRAZO_DE_RESPOSTA,
  ROTULO_DO_PACIENTE_PROVISORIO,
  ROTULOS_DO_ITEM_DA_PENDENCIA,
  ROTULOS_DO_MOTIVO_DA_PENDENCIA,
  ROTULOS_DA_RAZAO_DA_IMPLANTACAO,
  ROTULOS_DE_MODALIDADE,
  ROTULOS_DE_CAMPO,
  ROTULOS_DE_ORIGEM,
  ROTULOS_DE_SENTIDO,
  ROTULOS_DO_OBJETO,
  ehCampoClinico,
  formatarDuracaoEmHorasEMinutos,
  rotuloDoDono,
  type CampoExtraidoDoCaso,
  type CasoDetalhado,
  type ObjetoDaLeitura,
} from './api/tipos';

/**
 * Os relógios do caso: RESPOSTA (prazo copiado do ticket de triagem) e IMPLANTAÇÃO (da autorização). Cada um diz de
 * onde veio (prazo sem origem é erro silencioso) e, sem prazo de implantação, POR QUÊ, nunca um número padrão.
 */
function RelogiosDoCaso({ caso }: { caso: CasoDetalhado }): React.JSX.Element {
  return (
    <>
      <p className="m-0 mb-2 text-[0.8125rem] text-[var(--tinta-2)]">
        Resposta: da chegada do pedido até devolver o processo pronto. Implantação: da autorização até o paciente estar
        em casa.
      </p>
      <RelogiosEmLista caso={caso} />
    </>
  );
}

function RelogiosEmLista({ caso }: { caso: CasoDetalhado }): React.JSX.Element {
  return (
    <dl className="anery-ficha-caso">
      <dt>Resposta</dt>
      <dd>
        <SeloDaSituacaoDoPrazo situacao={caso.situacaoDaResposta} />
      </dd>
      {caso.prazoDeRespostaEm !== null ? (
        <>
          <dt>Prazo de resposta</dt>
          <dd>{formatarDataHora(caso.prazoDeRespostaEm)}</dd>
          <dt>Origem do prazo</dt>
          <dd>{ORIGEM_DO_PRAZO_DE_RESPOSTA}</dd>
        </>
      ) : (
        <>
          <dt>Origem do prazo</dt>
          <dd>o ticket de triagem de origem não tinha prazo de resposta</dd>
        </>
      )}
      {caso.respondidoEm !== null ? (
        <>
          <dt>Respondido em</dt>
          <dd>{formatarDataHora(caso.respondidoEm)}</dd>
        </>
      ) : null}
      {caso.espera !== null && caso.espera.pausaDeRespostaSegundos > 0 ? (
        <>
          <dt>Pausado por espera</dt>
          <dd>{formatarDuracaoEmHorasEMinutos(caso.espera.pausaDeRespostaSegundos)}</dd>
        </>
      ) : null}

      <dt>Implantação</dt>
      <dd>
        {caso.prazoDeImplantacaoEm !== null ? (
          <SeloDaSituacaoDoPrazo situacao={caso.situacaoDaImplantacao} />
        ) : (
          ROTULOS_DA_RAZAO_DA_IMPLANTACAO[caso.razaoDaImplantacao]
        )}
      </dd>
      {caso.autorizadoEm !== null ? (
        <>
          <dt>Autorizado em</dt>
          <dd>{formatarDataHora(caso.autorizadoEm)}</dd>
        </>
      ) : null}
      {caso.prazoDeImplantacaoEm !== null ? (
        <>
          <dt>Prazo de implantação</dt>
          <dd>
            {formatarDataHora(caso.prazoDeImplantacaoEm)}
            {caso.horasDeImplantacao !== null ? ` · ${String(caso.horasDeImplantacao)} h` : ''}
          </dd>
          <dt>Origem do prazo de implantação</dt>
          <dd>
            {ORIGEM_DO_PRAZO_DE_IMPLANTACAO}
            {caso.modalidade !== null
              ? ` (${ROTULOS_DE_MODALIDADE[caso.modalidade]}${caso.ventilacaoContinua ? ', com ventilação contínua' : ''})`
              : ''}
          </dd>
        </>
      ) : null}
      {caso.espera !== null && caso.espera.pausaDeImplantacaoSegundos > 0 ? (
        <>
          <dt>Pausado por espera (implantação)</dt>
          <dd>{formatarDuracaoEmHorasEMinutos(caso.espera.pausaDeImplantacaoSegundos)}</dd>
        </>
      ) : null}
    </dl>
  );
}

/**
 * Os avisos do cadastro que a completude nao cobre (`TP-040`): a operadora sem curadoria e a SUGESTAO de vinculo com o
 * IW (o IW achou o CPF; vincular e de uma pessoa). O que falta, item a item, e o "O que falta para completar" (`TP-058`):
 * a lista de pendencias saiu da tela para nao haver duas contas do mesmo Caso.
 */
function AvisosDoCadastro({ caso }: { caso: CasoDetalhado }): React.JSX.Element | null {
  const sugestoes = caso.pendencias.filter((pendencia) => pendencia.estado === 'Open' && pendencia.motivo !== null);
  const naoCurado = caso.frentes !== null && !caso.frentes.curado;
  if (!naoCurado && sugestoes.length === 0) return null;

  return (
    <div className="mt-3 space-y-1">
      {/* O Caso de operadora NAO CURADA diz isso. Trilha vazia parece completa, e essa mentira ja foi paga uma vez
          (TP-043). */}
      {naoCurado && (
        <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">
          A operadora deste Caso ainda não tem as frentes da captação configuradas. O que aparece aqui é só o cadastro.
        </p>
      )}
      {sugestoes.map((pendencia) => (
        <p key={pendencia.item} className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">
          <b className="text-[var(--tinta)]">{ROTULOS_DO_ITEM_DA_PENDENCIA[pendencia.item]}:</b>{' '}
          {pendencia.motivo !== null ? ROTULOS_DO_MOTIVO_DA_PENDENCIA[pendencia.motivo] : ''}
        </p>
      ))}
    </div>
  );
}

interface GrupoDeCampos {
  readonly objetoTipo: ObjetoDaLeitura;
  readonly objetoId: string;
  readonly rotulo: string | null;
  readonly campos: CampoExtraidoDoCaso[];
}

/**
 * Agrupa por e-mail/anexo de origem, na ordem em que chegaram — sem
 * reconciliar leituras concorrentes do mesmo campo (`D1` do tech-plan).
 */
function agruparPorObjeto(campos: readonly CampoExtraidoDoCaso[]): readonly GrupoDeCampos[] {
  const porChave = new Map<string, GrupoDeCampos>();
  for (const campo of campos) {
    const chave = `${campo.objetoTipo}:${campo.objetoId}`;
    const grupo = porChave.get(chave);
    if (grupo) {
      grupo.campos.push(campo);
    } else {
      porChave.set(chave, {
        objetoTipo: campo.objetoTipo,
        objetoId: campo.objetoId,
        rotulo: campo.rotuloDeOrigem,
        campos: [campo],
      });
    }
  }
  return [...porChave.values()];
}

/**
 * Uma leitura. Campo clínico (`cid`/`hd`) nunca mostra valor nem confiança —
 * mesma disciplina de `CampoClinico` da triagem, só `encontrado` aparece.
 * Origem e "revisão pendente" são sempre TEXTO, nunca só cor.
 */
function CampoExtraido({ campo }: { campo: CampoExtraidoDoCaso }): React.JSX.Element {
  if (ehCampoClinico(campo.campo)) {
    return (
      <p className="text-[0.8125rem] text-[var(--tinta-2)]">
        <b className="font-semibold text-[var(--tinta)]">{ROTULOS_DE_CAMPO[campo.campo]}</b>{' '}
        {campo.encontrado ? 'encontrado' : 'não encontrado'} — valor clínico não é exibido.
      </p>
    );
  }

  // Mesmo gate de CpfPromovivel (PromoteTicketToCaseHandler.cs): confiança
  // NÃO MEDIDA (null) também precisa de revisão, não só confiança medida
  // abaixo do limiar — achado do code-reviewer (28/09), o front tratava só
  // o segundo caso e mostrava "0%" (parece medido em zero) pro primeiro.
  const pendente =
    campo.encontrado &&
    campo.valorHumano === null &&
    (campo.confianca === null || (campo.limiar !== null && campo.confianca < campo.limiar));

  return (
    <div>
      <p className="text-[0.8125rem]">
        <b className="font-semibold text-[var(--tinta)]">{ROTULOS_DE_CAMPO[campo.campo]}</b>:{' '}
        {campo.encontrado ? (campo.valorHumano ?? campo.valor ?? '—') : 'não encontrado'}
        {campo.origem !== null ? (
          <span className="ml-1.5 text-[0.75rem] text-[var(--tinta-3)]">· {ROTULOS_DE_ORIGEM[campo.origem]}</span>
        ) : null}
        {pendente ? <span className="ml-1.5 text-[0.75rem] text-[var(--roxo)]">· revisão pendente</span> : null}
      </p>
      {campo.encontrado && campo.confianca !== null ? (
        <Medida pct={Math.round(campo.confianca * 100)} rotulo="Confiança da leitura" />
      ) : null}
      {campo.encontrado && campo.confianca === null ? (
        <p className="text-[0.75rem] text-[var(--tinta-3)]">Confiança não medida</p>
      ) : null}
    </div>
  );
}

/** Um fato da ficha do Caso: rótulo pequeno em cima, valor embaixo. `largo` ocupa a linha toda (formulário aberto). */
function FatoDoCaso({
  rotulo,
  largo = false,
  children,
}: {
  rotulo: string;
  largo?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={largo ? 'col-span-full min-w-0' : 'min-w-0'}>
      <dt className="text-[0.6875rem] uppercase tracking-[0.07em] text-[var(--tinta-3)]">{rotulo}</dt>
      <dd className="m-0 mt-0.5 text-sm text-[var(--tinta)]">{children}</dd>
    </div>
  );
}

/**
 * As partes do Caso. A ficha e as ações ficam ACIMA das abas: em qualquer uma delas a pessoa sabe de qual Caso se trata.
 * O que é do dia (relógios, cadastro, frentes, esperas, modalidade) é a visão geral; o resto, que se consulta, tem aba.
 * A aba vai para a URL (`?aba=`), para o link levar direto a ela.
 */
const ABAS_DO_CASO = [
  { chave: 'visao-geral', rotulo: 'Visão geral' },
  { chave: 'dados', rotulo: 'Informações do caso' },
  { chave: 'orcamento', rotulo: 'Orçamento' },
  { chave: 'email', rotulo: 'E-mail' },
  { chave: 'linha-do-tempo', rotulo: 'Linha do tempo' },
] as const;

type AbaDoCaso = (typeof ABAS_DO_CASO)[number]['chave'];

function lerAba(valor: string | null): AbaDoCaso {
  return ABAS_DO_CASO.find((a) => a.chave === valor)?.chave ?? 'visao-geral';
}

/**
 * O caso, com o que a API entrega — e nada além disso.
 *
 * Saíram daqui, porque não existem no contrato: protocolo, operadora,
 * iniciais do beneficiário, responsável, observação e correlation_id do caso.
 */
export function DetalheDoCaso(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  // De onde a pessoa veio. `state` é dado de navegação, não contrato: link
  // colado na barra de endereço chega sem ele, e aí vale o padrão.
  const navegacao = useLocation();
  const origem = (navegacao.state as { origem?: OrigemDoCaso } | null)?.origem ?? ORIGEM_PADRAO;
  const consulta = useQuery(opcoesDoCaso(id));
  const threadConsulta = useQuery(opcoesDaThreadDoCaso(id));
  const camposConsulta = useQuery(opcoesDosCamposExtraidosDoCaso(id));
  const linhaDoTempoConsulta = useQuery(opcoesDaLinhaDoTempoDoCaso(id));

  const [parametros, definirParametros] = useSearchParams();
  const aba = lerAba(parametros.get('aba'));
  const trocarAba = (nova: AbaDoCaso): void => {
    // `state` acompanha: é ele que diz de onde a pessoa veio, e o "Voltar" não pode esquecer isso ao trocar de aba.
    definirParametros(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (nova === 'visao-geral') proximos.delete('aba');
        else proximos.set('aba', nova);
        return proximos;
      },
      { replace: true, state: navegacao.state as unknown },
    );
  };

  const [alterandoModalidade, definirAlterandoModalidade] = useState(false);
  const [menuDoCaso, definirMenuDoCaso] = useState<HTMLElement | null>(null);
  // Conta os registros de alçada feitos nesta tela: o aviso "registre quem aprovou" do botão de entrega some com eles.
  const [alcadasRegistradas, definirAlcadasRegistradas] = useState(0);
  const [confirmando, definirConfirmando] = useState(false);
  const [confirmado, definirConfirmado] = useState(false);
  const clienteDeConsultas = useQueryClient();

  const caso = consulta.data ?? null;

  // O servidor devolve o detalhe ATUALIZADO a cada escrita; o resto do Caso (completude, linha do tempo, documentos) e
  // o quadro são relidos, porque a escrita pode ter fechado sozinha um passo já provado (TP-058, D18).
  const aoAtualizar = (atualizado: CasoDetalhado): void => {
    clienteDeConsultas.setQueryData(chavesDeCasos.detalhe(id), atualizado);
    void invalidarOQueAProvaMuda(clienteDeConsultas, id, 'detalhe');
  };
  const aoRecarregar = (): void => {
    void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.detalhe(id) });
  };

  return (
    <div className="anery-marca" style={variaveisDaMarca}>
      <div className="space-y-6">
        <Button
          component={LinkDeRota}
          to={origem.caminho}
          startIcon={<ArrowBackOutlined />}
          sx={{ alignSelf: 'start' }}
        >
          {origem.rotulo}
        </Button>

        {consulta.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

        {consulta.isError ? (
          <EstadoDeErro
            erro={consulta.error}
            contexto="Não foi possível carregar o caso"
            aoReprocessar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}

        {/* A API respondeu 404: o caso não existe. Não é erro e não é tela
            em branco — é um estado com nome. */}
        {consulta.isSuccess && caso === null ? (
          <EstadoVazio
            titulo="Caso não encontrado"
            descricao="A API respondeu normalmente, mas não há caso com este identificador para o seu núcleo. O identificador pode estar errado, ou o caso ser de outro núcleo."
          />
        ) : null}

        {consulta.isSuccess && caso !== null ? (
          <>
            {/* A ficha do Caso: QUEM é (o paciente, no título), o gesto do dia à direita, e os fatos que o definem
                numa faixa só. Reprocessar é raro e ainda não dispara nada: mora no menu do canto. */}
            <section
              className="overflow-hidden rounded-lg border border-solid border-[var(--fio)] bg-[var(--chao)]"
              aria-label="Ficha do caso"
            >
              <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-0 border-b border-solid border-[var(--fio)] bg-[var(--chao-2)] px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-[0.6875rem] uppercase tracking-[0.07em] text-[var(--tinta-3)]">Caso de</p>
                  <h1 className="cond m-0 text-lg font-semibold text-[var(--navy)]">
                    {caso.pacienteNome ?? <Identificador valor={caso.pacienteId} />}
                    {caso.pacienteProvisorio ? (
                      <span className="ml-2 text-xs font-normal text-[var(--tinta-2)]">
                        ({ROTULO_DO_PACIENTE_PROVISORIO})
                      </span>
                    ) : null}
                  </h1>
                </div>
                <TransicoesDoCaso
                  caso={caso}
                  aoAtualizar={aoAtualizar}
                  aoRecarregar={aoRecarregar}
                  reiniciarAvisoQuando={alcadasRegistradas}
                />
                <IconButton
                  size="small"
                  aria-label="Mais ações do caso"
                  aria-haspopup="menu"
                  onClick={(e) => {
                    definirMenuDoCaso(e.currentTarget);
                  }}
                >
                  <MoreVertOutlined fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={menuDoCaso}
                  open={menuDoCaso !== null}
                  onClose={() => {
                    definirMenuDoCaso(null);
                  }}
                >
                  {/* Nenhuma ação externa sem clique humano explícito: o item ABRE o diálogo; quem dispara é a
                      confirmação. */}
                  <MenuItem
                    onClick={() => {
                      definirMenuDoCaso(null);
                      definirConfirmando(true);
                    }}
                  >
                    Reprocessar caso
                  </MenuItem>
                </Menu>
              </header>

              <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-3 lg:grid-cols-[1fr_1fr_1.6fr_1fr_1fr]">
                {/* `rotuloDoEstado` e não `ROTULOS_DE_ESTADO[...]`: `estado` é `string` desde o TP-043 (F4 da
                    BR-006), e o Record não indexa. */}
                <FatoDoCaso rotulo="Estado">{rotuloDoEstado(caso.estado)}</FatoDoCaso>
                <FatoDoCaso rotulo="Dono">{rotuloDoDono(caso)}</FatoDoCaso>
                <FatoDoCaso
                  rotulo="Modalidade"
                  largo={caso.modalidade === null || caso.estado === 'Closed' || alterandoModalidade}
                >
                  {/* Já definida, lê-se como texto e muda por "Alterar"; sem definição (ou encerrado), o formulário
                      aparece direto, porque é ele que diz o que fazer. */}
                  {caso.modalidade !== null && caso.estado !== 'Closed' && !alterandoModalidade ? (
                    <>
                      <span className="inline-flex items-center gap-1">
                        {ROTULOS_DE_MODALIDADE[caso.modalidade]}
                        <IconButton
                          size="small"
                          aria-label="Alterar a modalidade"
                          sx={{ p: 0.25 }}
                          onClick={() => {
                            definirAlterandoModalidade(true);
                          }}
                        >
                          <EditOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                      {/* Segunda linha de propósito: é um atributo da modalidade, não parte do nome dela. */}
                      {caso.modalidade === 'ID' && caso.ventilacaoContinua ? (
                        <span className="block text-[0.75rem] text-[var(--tinta-2)]">Ventilação contínua</span>
                      ) : null}
                    </>
                  ) : (
                    <div className="mt-1 space-y-1">
                      {/* `key` remonta o formulário quando o servidor devolve outra modalidade (salvou, ou
                          recarregou após um conflito). */}
                      <ModalidadeDoCasoForm
                        key={`${caso.modalidade ?? 'nenhuma'}-${String(caso.ventilacaoContinua)}-${caso.estado}`}
                        caso={caso}
                        aoAtualizar={(atualizado) => {
                          aoAtualizar(atualizado);
                          definirAlterandoModalidade(false);
                        }}
                        aoRecarregar={aoRecarregar}
                      />
                      {alterandoModalidade ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            definirAlterandoModalidade(false);
                          }}
                        >
                          Cancelar
                        </Button>
                      ) : null}
                    </div>
                  )}
                </FatoDoCaso>
                <FatoDoCaso rotulo="E-mail recebido às">
                  <span className="num">
                    {caso.emailRecebidoEm === null ? '—' : formatarDataHora(caso.emailRecebidoEm)}
                  </span>
                </FatoDoCaso>
                <FatoDoCaso rotulo="Criado em">
                  <span className="num">{formatarDataHora(caso.criadoEm)}</span>
                </FatoDoCaso>
                <FatoDoCaso rotulo="Atualizado em">
                  <span className="num">{formatarDataHora(caso.atualizadoEm)}</span>
                </FatoDoCaso>
              </dl>

              {confirmado ? (
                <p className="m-0 border-0 border-t border-solid border-[var(--fio)] px-4 py-2 text-[0.75rem] text-[var(--tinta-2)]">
                  Confirmação registrada. Nada saiu daqui: o envio nasce quando a API expuser o reprocesso.
                </p>
              ) : null}
            </section>

            <ConfirmacaoHumana
              aberto={confirmando}
              titulo="Reprocessar este caso?"
              descricao="O reprocesso reenvia a execução ao robô com o correlation_id ORIGINAL — ele é lido da linha de execução no servidor, carregado, nunca regenerado. A API ainda não expõe esse identificador nem o endpoint de reprocesso, então esta confirmação não dispara nada."
              rotuloDeConfirmar="Reprocessar"
              aoCancelar={() => {
                definirConfirmando(false);
              }}
              aoConfirmar={() => {
                definirConfirmando(false);
                definirConfirmado(true);
              }}
            />

            <div className="border-0 border-b border-solid border-[var(--fio)]">
              <Tabs
                value={aba}
                onChange={(_evento, nova: AbaDoCaso) => {
                  trocarAba(nova);
                }}
                aria-label="Partes do caso"
                variant="scrollable"
                allowScrollButtonsMobile
              >
                {ABAS_DO_CASO.map((a) => (
                  <Tab
                    key={a.chave}
                    value={a.chave}
                    id={`aba-${a.chave}`}
                    aria-controls={`painel-${a.chave}`}
                    label={a.rotulo}
                  />
                ))}
              </Tabs>
            </div>

            <div role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`} className="space-y-6">
              {aba === 'visao-geral' && (
                <>
                  <Painel titulo="Relógios">
                    <RelogiosDoCaso caso={caso} />
                  </Painel>

                  <Painel titulo="O que falta para completar">
                    <OQueFaltaParaCompletar casoId={caso.id} />
                    <AvisosDoCadastro caso={caso} />
                  </Painel>

                  <Painel titulo="Vínculo com o cadastro do IW">
                    {/* `key`: o estado de busca e de erro é de UM Caso; trocar de /casos/:id não pode herdá-lo. */}
                    <VinculoComOIw key={caso.id} caso={caso} aoAtualizar={aoAtualizar} />
                  </Painel>

                  {/* NR-255: o que o Caso mandou ao IW e o que aconteceu depois do clique. Só com envio. */}
                  <EnviosAoIw casoId={caso.id} />

                  {/* Onde se FECHA um passo de frente. Fica aqui, e não no quadro,
                      porque é aqui que há trilha de quem fez o quê (BR-007 §1.2). */}
                  {caso.frentes?.curado === true && (
                    <Painel titulo="Frentes da captação">
                      <PainelDasFrentes caso={caso} aoAtualizar={aoAtualizar} />
                    </Painel>
                  )}

                  {/* Autorizado: registra. Encerrado: só se houve passagem, que continua à vista com as divergências
                      abertas e a validação pendente ainda operáveis (a API aceita as duas em qualquer estado). */}
                  {(caso.estado === 'Authorized' || caso.estado === 'Closed') && (
                    <PainelDaPassagemDePlantao casoId={caso.id} soSeHouver={caso.estado === 'Closed'} />
                  )}

                  <Painel
                    titulo="Retornos da autorização"
                    sub="O que a carta da SIAI decidiu. Nada muda no caso sem um clique; a hora gravada é a da chegada do e-mail."
                  >
                    <RetornosDoCaso casoId={caso.id} />
                  </Painel>

                  <Painel titulo="Esperas">
                    <EsperasDoCaso caso={caso} aoAtualizar={aoAtualizar} />
                  </Painel>
                </>
              )}

              {aba === 'dados' && (
                <Painel
                  titulo="Informações do caso"
                  sub="O que os documentos validados, os e-mails e o IW dizem do paciente. Cada valor diz de onde veio."
                >
                  <InformacoesDoCaso casoId={caso.id} />
                </Painel>
              )}

              {aba === 'orcamento' && (
                <>
                  <Painel titulo="Orçamento no IW">
                    {/* `key` remonta a seção quando a modalidade do Caso muda: a conferência nunca segue com a antiga. */}
                    <OrcamentoDoIw
                      key={`${caso.id}-${caso.modalidade ?? 'nenhuma'}-${String(caso.ventilacaoContinua)}`}
                      casoId={caso.id}
                      modalidadeDoCaso={modalidadeDoMotorDoCaso(caso.modalidade, caso.ventilacaoContinua)}
                    />
                  </Painel>

                  <Painel
                    titulo="Alçada da margem"
                    sub="Sem o registro de quem aprovou a margem, o orçamento não vai para a operadora."
                  >
                    <AlcadaDaMargem
                      casoId={caso.id}
                      aoRegistrar={() => {
                        definirAlcadasRegistradas((n) => n + 1);
                      }}
                    />
                  </Painel>
                </>
              )}

              {aba === 'email' && (
                <>
                  <Painel titulo="Thread de e-mail">
                    {threadConsulta.isPending ? <EstadoDeCarregamento linhas={2} /> : null}

                    {threadConsulta.isError ? (
                      <EstadoDeErro
                        erro={threadConsulta.error}
                        contexto="Não foi possível carregar a thread de e-mail"
                        aoReprocessar={() => {
                          void threadConsulta.refetch();
                        }}
                      />
                    ) : null}

                    {/* Caso sem ticket apontando (dado anterior à promoção existir,
                        TP-023) é estado nomeado, não erro — a API devolve 200 com
                        lista vazia, e a tela diz isso em vez de esconder a seção. */}
                    {threadConsulta.isSuccess && threadConsulta.data.length === 0 ? (
                      <EstadoVazio
                        titulo="Sem thread de e-mail"
                        descricao="Nenhum ticket deste caso tem e-mail vinculado — caso anterior à promoção automática (TP-023), ou o vínculo ainda não existe."
                      />
                    ) : null}

                    {threadConsulta.isSuccess && threadConsulta.data.length > 0 ? (
                      <ul className="list-none pl-0">
                        {threadConsulta.data.map((mensagem) => (
                          <li key={mensagem.emailId} className="anery-item">
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium">{mensagem.assunto ?? '(sem assunto)'}</span>
                              <span className="block text-xs text-[var(--tinta-2)]">
                                {ROTULOS_DE_SENTIDO[mensagem.sentido]}
                              </span>
                            </span>
                            <span className="num shrink-0 text-xs text-[var(--tinta-3)]">
                              {formatarDataHora(mensagem.recebidoEm)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </Painel>

                  <Painel titulo="Campos extraídos">
                    {camposConsulta.isPending ? <EstadoDeCarregamento linhas={3} /> : null}

                    {camposConsulta.isError ? (
                      <EstadoDeErro
                        erro={camposConsulta.error}
                        contexto="Não foi possível carregar os campos extraídos"
                        aoReprocessar={() => {
                          void camposConsulta.refetch();
                        }}
                      />
                    ) : null}

                    {/* Mesma disciplina da thread: estado nomeado, nunca seção ausente. */}
                    {camposConsulta.isSuccess && camposConsulta.data.length === 0 ? (
                      <EstadoVazio
                        titulo="Sem campos extraídos"
                        descricao="Nenhum ticket deste caso tem campo lido — o caso é de antes da promoção automática existir (TP-023), ou a leitura ainda não encontrou nada."
                      />
                    ) : null}

                    {camposConsulta.isSuccess && camposConsulta.data.length > 0 ? (
                      <div className="space-y-4">
                        {agruparPorObjeto(camposConsulta.data).map((grupo) => (
                          <div key={`${grupo.objetoTipo}:${grupo.objetoId}`}>
                            <p className="mb-1.5 text-[0.6875rem] uppercase tracking-wider text-[var(--tinta-3)]">
                              {ROTULOS_DO_OBJETO[grupo.objetoTipo]} · {grupo.rotulo ?? '(sem assunto)'}
                            </p>
                            <div className="space-y-2">
                              {grupo.campos.map((campo) => (
                                <CampoExtraido key={`${campo.campo}-${String(campo.ordem)}`} campo={campo} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </Painel>
                </>
              )}

              {aba === 'linha-do-tempo' && (
                <>
                  <Painel titulo="Linha do tempo">
                    {linhaDoTempoConsulta.isPending ? <EstadoDeCarregamento linhas={3} /> : null}

                    {linhaDoTempoConsulta.isError ? (
                      <EstadoDeErro
                        erro={linhaDoTempoConsulta.error}
                        contexto="Não foi possível carregar a linha do tempo"
                        aoReprocessar={() => {
                          void linhaDoTempoConsulta.refetch();
                        }}
                      />
                    ) : null}

                    {linhaDoTempoConsulta.isSuccess && linhaDoTempoConsulta.data.length === 0 ? (
                      <EstadoVazio
                        titulo="Sem eventos"
                        descricao="A API respondeu normalmente; nenhum evento foi registrado para este caso ainda."
                      />
                    ) : null}

                    {linhaDoTempoConsulta.isSuccess && linhaDoTempoConsulta.data.length > 0 ? (
                      <ul className="list-none pl-0">
                        {linhaDoTempoConsulta.data.map((evento, indice) => (
                          <li
                            key={`${evento.tipo}-${evento.ocorreuEm.toISOString()}-${String(indice)}`}
                            className="anery-item"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium">{rotuloDoEventoDaLinhaDoTempo(evento)}</span>
                              <span className="block text-xs text-[var(--tinta-2)]">
                                {evento.atorNome ?? 'Rotina automática'}
                              </span>
                            </span>
                            <span className="num shrink-0 text-xs text-[var(--tinta-3)]">
                              {formatarDataHora(evento.ocorreuEm)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </Painel>
                </>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
